
import React from "react";
import { ClipboardIcon } from "@heroicons/react/24/outline"; 
import { FiChevronRight } from "react-icons/fi";

const CodeBlock = ({ code }) => {
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative bg-black/90 rounded-lg overflow-hidden mb-4">
      <div className="absolute top-2 right-2 z-10">
        <button
          onClick={copyToClipboard}
          className="p-1.5 rounded text-white"
          title="Copy to clipboard"
        >
          {copied ? (
            <span className="text-green-400 text-lg leading-none">✔</span>
          ) : (
            <ClipboardIcon className="w-4 h-4" />
          )}
        </button>
      </div>

      <pre className="text-xs text-green-400 p-4 pt-8 font-mono overflow-x-auto">
        {code}
      </pre>
    </div>
  );
};

export function renderKafkaMonitoring () {
    return (
      <div>
        <main className="transition-opacity duration-300">
          <div className="py-4">
    
            {/* Breadcrumb */}
            <nav className="flex flex-wrap items-center gap-2 text-sm text-[#9a734c] mb-8">
              <a className="hover:text-primary hover:underline" href="#">Netraa APM</a>
              <FiChevronRight />
              <span>Agent Setup Guide</span>
              <FiChevronRight />
              <span>Message Queue</span>
              <FiChevronRight />
              <span className="text-[#1b140d] dark:text-white font-medium">Kafka Monitoring</span>
            </nav>
    
            {/* Intro */}
            <div className="mb-10" id="Introduction">
              <span className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-wider text-orange-600 uppercase bg-orange-100 rounded-full dark:bg-orange-900/30 dark:text-orange-400">
                Documentation
              </span>
    
              <h1 className="text-3xl lg:text-4xl font-extrabold text-text-main dark:text-white tracking-tight mb-4">
                Kafka Monitoring using{" "}
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-red-600">
                  Netraa APM
                </span>
              </h1>
    
              <p className="text-lg text-text-muted dark:text-gray-400 leading-relaxed">
                This document provides end-to-end steps to enable monitoring for
                Apache Kafka brokers using the Netraa APM Kafka Agent and JMX exporter.
              </p>
            </div>
    
            {/* Prerequisites */}
            <section className="mb-12 scroll-mt-24" id="Prerequisites">
              <h2 className="text-2xl font-bold text-text-main dark:text-white mb-6 flex items-center gap-3">
                <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 border border-blue-200 dark:border-blue-900/50">
                  <span className="material-symbols-outlined text-[24px]">fact_check</span>
                </span>
                Prerequisites
              </h2>
    
              <div className="prose dark:prose-invert max-w-none text-sm">
                <p className="text-text-muted dark:text-gray-400 mb-4">
                  Ensure the following components are available before enabling Kafka monitoring.
                </p>
    
                <ul className="list-disc pl-5 space-y-2 text-text-muted dark:text-gray-400">
                  <li>Apache Kafka broker installed and running</li>
                  <li>Java installed (same version used by Kafka)</li>
                  <li>Kafka agent package (<code>kafka_agent.zip</code>)</li>
                </ul>
              </div>
            </section>
    
            {/* Step 1 */}
            <section className="mb-12 scroll-mt-24" id="steps to follow">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-black bg-primary text-white w-8 h-8 rounded flex items-center justify-center shrink-0">1</span>
                <h2 className="text-xl font-bold text-text-main dark:text-white">
                  Extract Kafka Agent
                </h2>
              </div>
    
              <p className="text-sm text-text-muted mb-4">
                Download the Kafka agent from the ApMoSys SharePoint repository.
                Copy the file to the Kafka broker host and extract it.
              </p>
    
              <CodeBlock
                code={`# Copy kafka agent to Kafka broker host
    scp kafka_agent.zip <kafka-host>:/opt/
    
    # Extract the agent
    unzip kafka_agent.zip`}
              />
            </section>
    
            {/* Step 2 */}
            <section className="mb-12 scroll-mt-24">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-black bg-primary text-white w-8 h-8 rounded flex items-center justify-center shrink-0">2</span>
                <h2 className="text-xl font-bold text-text-main dark:text-white">
                  Configure Kafka Broker with JMX Exporter
                </h2>
              </div>
    
              <p className="text-sm text-text-muted mb-4">
                Edit the Kafka broker startup configuration and add JVM options to enable
                JMX exporter metrics exposure.
              </p>
    
              <p className="text-xs font-bold text-text-muted uppercase mb-2">
                Edit File
              </p>
    
              <CodeBlock
                code={`<KAFKA_HOME>/bin/kafka-server-start.sh`}
              />
    
              <p className="text-xs font-bold text-text-muted uppercase mb-2 mt-4">
                Set Environment Variable
              </p>
    
              <CodeBlock
                code={`export KAFKA_OPTS="$KAFKA_OPTS \
    -javaagent:/path/to/jmx_prometheus_javaagent.jar=7071:/path/to/kafka-jmx.yml \
    -Dcom.sun.management.jmxremote \
    -Dcom.sun.management.jmxremote.authenticate=false \
    -Dcom.sun.management.jmxremote.ssl=false"`}
              />
            </section>
    
            {/* Step 3 */}
            <section className="mb-12 scroll-mt-24">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-black bg-primary text-white w-8 h-8 rounded flex items-center justify-center shrink-0">3</span>
                <h2 className="text-xl font-bold text-text-main dark:text-white">
                  Restart Kafka Broker
                </h2>
              </div>
    
              <p className="text-sm text-text-muted mb-4">
                Restart the Kafka broker to apply the JMX exporter configuration.
              </p>
    
              <CodeBlock
                code={`bin/kafka-server-stop.sh
    
    bin/kafka-server-start.sh config/server.properties`}
              />
    
              <p className="text-sm text-text-muted mt-4 mb-2">
                Verify that metrics are exposed:
              </p>
    
              <CodeBlock
                code={`curl http://localhost:7071/metrics`}
              />
    
              <p className="text-xs text-text-muted">
                You should see Kafka JMX metrics output.
              </p>
            </section>
    
            {/* Step 4 */}
            <section className="mb-12 scroll-mt-24">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-black bg-primary text-white w-8 h-8 rounded flex items-center justify-center shrink-0">4</span>
                <h2 className="text-xl font-bold text-text-main dark:text-white">
                  Run Kafka Agent
                </h2>
              </div>
    
              <p className="text-sm text-text-muted mb-4">
                Start the Kafka monitoring agent which collects metrics and sends them to Netraa.
              </p>
    
              <CodeBlock
                code={`./kafka_agent.sh`}
              />
            </section>
    
            {/* Step 5 */}
            <section className="mb-12 scroll-mt-24">
              <div className="flex items-center gap-4 mb-4">
                <span className="text-sm font-black bg-primary text-white w-8 h-8 rounded flex items-center justify-center shrink-0">5</span>
                <h2 className="text-xl font-bold text-text-main dark:text-white">
                  Verify Metrics Flow
                </h2>
              </div>
    
              <ul className="list-disc pl-5 space-y-2 text-sm text-text-muted dark:text-gray-400">
                <li>
                  Confirm Kafka metrics are accessible at  
                  <code className="ml-1">http://localhost:7071/metrics</code>
                </li>
                <li>
                  Validate Kafka metrics are visible in the Netraa APM dashboard
                </li>
              </ul>
            </section>
    
            {/* Notes */}
            <section className="scroll-mt-24">
              <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 p-4 rounded-r-lg">
                <div className="flex items-center gap-2 mb-1">
                  <span className="material-symbols-outlined text-amber-600 text-sm">
                    warning
                  </span>
                  <h4 className="text-sm font-bold text-amber-800 dark:text-amber-300">
                    Notes
                  </h4>
                </div>
    
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  One JMX exporter instance must run per Kafka broker in order to expose
                  broker-specific metrics correctly.
                </p>
              </div>
            </section>
    
          </div>
        </main>
      </div>
    );
    }